import { LitElement, html, css } from 'lit'
import { classMap } from 'lit/directives/class-map.js'

class HHButton extends LitElement {
  static properties = {
    key: {},
    href: { type: String },
    decoration: { type: String },
    spacing: { type: String },
    type: { type: String },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    underline: {
      converter(value) {
        return value === 'true' ? true : false
      },
    },
    onclick: {
      type: String,
      attribute: 'custom:click',
    },
  }

  constructor() {
    super()

    this.underline = true
    this.disabled = false
  }

  onClick(e) {
    if (!this.onclick) {
      return
    }

    this.dispatchEvent(
      new CustomEvent(this.onclick, {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          event: e,
        },
      })
    )
  }

  render() {
    return html`${!this.href
      ? html`<button
          class="${classMap({
        underline: this.underline,
      })}"
          ?disabled="${this.disabled}"
          @click="${this.onClick}"
          type="${this.type || 'button'}"
          value="Submit"
        >
          <slot></slot>
        </button>`
      : html`<a
          class="inline-flex ${classMap({
        underline: this.underline,
      })}"
          href="${this.href}"
          @click="${this.onClick}"
        >
          <slot></slot>
        </a>`}`
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .inline-flex {
      display: inline-flex;
    }

    button {
      max-width: var(--hh-max-width, 160px);
    }

    :host([width='full']) button {
      width: 100%;
      max-width: none;
    }

    :host([width='full']) a {
      width: 100%;
      max-width: none;
      justify-content: center;
    }

    :host([theme='tertiary']) button {
      max-width: none;
    }

    button,
    a {
      cursor: pointer;
      color: var(--hh-color, #FDFCF8);
      background-color: var(--hh-background, #000);
      border: var(--hh-border-width, 0.1rem) solid var(--hh-border-color, #000);
      transition: all var(--hh-duration-default, 300ms);
      appearance: none;
      font: var(--hh-font);
      letter-spacing: var(--hh-letter-spacing);
      text-transform: var(--hh-text-transform);
      padding: var(--hh-padding, 1em 2em);
      text-decoration: none;
      min-height: var(--hh-min-height, 30px);
      width: var(--hh-min-width, initial);
      text-align: var(--hh-text-align, center);
    }

    button:hover:not(:disabled),
    a:hover:not(:disabled) {
      color: var(--hh-color-hover, #000);
      background-color: var(--hh-background-hover, #FDFCF8);
      border-color: var(--hh-border-color-hover);
    }

    a {
      display: inline-block;
    }

    button:focus-visible,
    a:focus-visible {
      transition: none;
      outline: var(--hh-width-focus, 0.2rem) solid var(--hh-color-focus);
      outline-offset: -0.1rem;
    }

    button:disabled {
      cursor: default;
      color: var(--hh-color-disabled);
      background-color: var(--hh-background-disabled);
      border-color: var(--hh-background-disabled);
    }

    :host([theme='tertiary']) button,
    :host([theme='tertiary']) a,
    :host([theme='quaternary']) button,
    :host([theme='quaternary']) a {
      position: relative;
      background-color: var(--hh-background, transparent);
      padding: var(--hh-padding, 0);
      gap: var(--hh-gap, 1rem);
    }

    :host([theme='tertiary']) button.underline:after,
    :host([theme='tertiary']) a.underline:after,
    :host([theme='quaternary']) button.underline:after,
    :host([theme='quaternary']) a.underline:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: var(--hh-border-bottom-offset, 0);
      background-color: currentColor;
      width: calc(100% - var(--hh-letter-spacing, 0px));
      height: var(--hh-underline-width, 1px);
      transition: all var(--hh-duration-default, 300ms);
    }

    :host([theme='quaternary']) button:focus-visible {
      outline: 2px solid var(--hh-color-focus);
    }

    @media screen and (min-width: 990px) {
      :host([theme='tertiary']) button:hover:not(:disabled).underline:after,
      :host([theme='tertiary']) a:hover:not(:disabled).underline:after {
        height: var(--hh-underline-width-hover, 2px);
        background-color: var(--hh-underline-color-hover, currentColor);
      }

      :host([theme='tertiary']) button:focus-visible.underline:after,
      :host([theme='tertiary']) a:focus-visible.underline:after,
      :host([theme='quaternary']) button:focus-visible.underline:after,
      :host([theme='quaternary']) a:focus-visible.underline:after {
        transition: none;
        height: var(--hh-underline-width-hover, 2px);
        background-color: var(--hh-underline-color-focus, currentColor);
      }
    }

    :host([theme='quaternary']) button:not(:focus-visible).underline:after,
    :host([theme='quaternary']) a:not(:focus-visible).underline:after {
      display: none;
    }

    :slotted() {
      font: var(--hh-font);
    }

    :host([decoration~='rounded']) button,
    :host([decoration~='rounded']) a {
      border-radius: var(--hh-border-radius, 4px);
    }

    :host([border~='none']) button,
    :host([border~='none']) a {
      border: none;
    }
  `
}

customElements.define('hh-button', HHButton)
